// @format
// The call modal's end screen: the outcome picker, copy-number, playback speed and the recording's
// waveform. prospect.ts renders the screen and saves the outcome.

const WAVE_BARS = 40;
const SPEEDS = [1, 1.25, 1.5, 2];
const MENU_GAP = 6;
const VIEWPORT_MARGIN = 8;
const bound = new WeakSet<HTMLElement>();

export function initCallEndScreen(root: HTMLElement): void {
  if (bound.has(root)) return;
  bound.add(root);

  root.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;

    const choice = target.closest<HTMLElement>("[data-outcome]");
    if (choice && root.contains(choice)) {
      chooseOutcome(root, choice.dataset.outcome || "");
      return;
    }

    if (target.closest(".call-end-trigger")) {
      toggleOutcomeMenu(root);
      return;
    }
    if (!target.closest(".call-end-picker")) closeOutcomeMenu(root);

    if (target.closest(".call-end-copy")) copyPhone(root);
    if (target.closest(".call-end-speed")) cycleSpeed(root);
  });

  root.addEventListener(
    "scroll",
    (event) => {
      if (!(event.target as HTMLElement).closest?.(".call-end-menu")) closeOutcomeMenu(root);
    },
    true
  );
}

export function closeOutcomeMenu(root: HTMLElement): boolean {
  const menu = root.querySelector<HTMLElement>(".call-end-menu");
  if (!menu || menu.hidden) return false;

  menu.hidden = true;
  root.querySelector(".call-end-trigger")?.setAttribute("aria-expanded", "false");
  return true;
}

function toggleOutcomeMenu(root: HTMLElement): void {
  const menu = root.querySelector<HTMLElement>(".call-end-menu");
  if (!menu) return;
  if (closeOutcomeMenu(root)) return;

  const trigger = root.querySelector<HTMLElement>(".call-end-trigger");
  menu.hidden = false;
  if (trigger) placeMenu(trigger, menu);
  trigger?.setAttribute("aria-expanded", "true");
  window.addEventListener("resize", () => closeOutcomeMenu(root), { once: true });
  (menu.querySelector<HTMLElement>("[aria-selected=true]") || menu.querySelector<HTMLElement>(".call-end-option"))?.focus();
}

function placeMenu(trigger: HTMLElement, menu: HTMLElement): void {
  const rect = trigger.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
  const above = rect.top - MENU_GAP - VIEWPORT_MARGIN;

  menu.style.left = `${rect.left}px`;
  menu.style.width = `${rect.width}px`;
  menu.style.maxHeight = "";
  const height = menu.offsetHeight;

  if (height <= below || below >= above) {
    menu.style.top = `${rect.bottom + MENU_GAP}px`;
    menu.style.maxHeight = `${Math.min(height, below)}px`;
  } else {
    const fit = Math.min(height, above);
    menu.style.top = `${rect.top - MENU_GAP - fit}px`;
    menu.style.maxHeight = `${fit}px`;
  }
}

function chooseOutcome(root: HTMLElement, outcome: string): void {
  const input = root.querySelector<HTMLInputElement>(".call-status-select");
  const option = root.querySelector<HTMLElement>(`.call-end-option[data-outcome="${outcome}"]`);
  if (!input || !option) return;

  input.value = outcome;
  root.querySelectorAll<HTMLElement>(".call-end-pill").forEach((pill) => {
    const on = pill.dataset.outcome === outcome;
    pill.classList.toggle("selected", on);
    pill.setAttribute("aria-pressed", String(on));
  });
  root.querySelectorAll<HTMLElement>(".call-end-option").forEach((opt) => {
    opt.setAttribute("aria-selected", String(opt === option));
  });

  const trigger = root.querySelector<HTMLElement>(".call-end-trigger");
  const text = root.querySelector<HTMLElement>(".call-end-trigger-text");
  const label = option.querySelector("span");
  if (trigger && text && label) {
    text.innerHTML = label.innerHTML;
    trigger.classList.add("chosen");
  }

  const helper = root.querySelector<HTMLElement>(".call-end-helper");
  if (helper) {
    helper.dataset.state = "ready";
    helper.textContent = "Ready to log";
  }

  const complete = root.querySelector<HTMLButtonElement>(".complete-call-btn");
  if (complete) complete.disabled = false;

  closeOutcomeMenu(root);
}

function copyPhone(root: HTMLElement): void {
  const button = root.querySelector<HTMLElement>(".call-end-copy");
  const number = root.querySelector<HTMLElement>(".call-end-phone-number");
  if (!button || !number) return;

  const copied = (): void => {
    button.classList.add("copied");
    window.setTimeout(() => button.classList.remove("copied"), 1500);
  };

  const text = (number.textContent || "").trim();
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(copied).catch(() => selectText(number));
  } else {
    selectText(number);
  }
}

function selectText(el: HTMLElement): void {
  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function cycleSpeed(root: HTMLElement): void {
  const button = root.querySelector<HTMLElement>(".call-end-speed");
  const audio = document.getElementById(button?.dataset.audioId || "") as HTMLAudioElement | null;
  if (!button || !audio) return;

  const next = SPEEDS[(SPEEDS.indexOf(audio.playbackRate) + 1) % SPEEDS.length] || 1;
  audio.defaultPlaybackRate = next;
  audio.playbackRate = next;
  button.textContent = `${next}x`;
}

// Bars come from the decoded recording; until (or unless) that works they sit flat.
export function drawWaveform(wave: HTMLElement, onLength?: (seconds: number) => void): void {
  renderBars(wave, new Array(WAVE_BARS).fill(0.3));

  const src = wave.dataset.src;
  const Context: typeof AudioContext | undefined = window.AudioContext || (window as any).webkitAudioContext;
  if (!src || !Context) return;

  fetch(src, { credentials: "same-origin" })
    .then((response) => (response.ok ? response.arrayBuffer() : Promise.reject(new Error(String(response.status)))))
    .then((data) => {
      const context = new Context();
      return context.decodeAudioData(data).finally(() => context.close());
    })
    .then((buffer) => {
      renderBars(wave, peaks(buffer, WAVE_BARS));
      if (onLength && Number.isFinite(buffer.duration)) onLength(buffer.duration);
    })
    .catch(() => undefined);
}

export function paintWaveform(wave: HTMLElement, ratio: number): void {
  const bars = wave.querySelectorAll<HTMLElement>(".call-end-wave-bar");
  const played = Math.round(Math.min(Math.max(ratio, 0), 1) * bars.length);
  bars.forEach((bar, i) => bar.classList.toggle("played", i < played));
  wave.setAttribute("aria-valuenow", String(Math.round(ratio * 100)));
}

function renderBars(wave: HTMLElement, heights: number[]): void {
  const played = wave.querySelectorAll(".call-end-wave-bar.played").length;
  wave.innerHTML = heights
    .map((h, i) => `<span class="call-end-wave-bar${i < played ? " played" : ""}" style="height:${Math.round(h * 100)}%"></span>`)
    .join("");
}

function peaks(buffer: AudioBuffer, count: number): number[] {
  const data = buffer.getChannelData(0);
  const block = Math.max(1, Math.floor(data.length / count));
  const raw: number[] = [];
  for (let i = 0; i < count; i++) {
    let max = 0;
    for (let j = i * block; j < Math.min((i + 1) * block, data.length); j++) {
      const v = Math.abs(data[j]);
      if (v > max) max = v;
    }
    raw.push(max);
  }
  const top = Math.max(...raw) || 1;
  return raw.map((v) => Math.max(0.12, v / top));
}
